import React, { FC, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { FormProviders, FormRequestTemplate } from '@typings/api-models';
import InputField from '@components/FormFields/InputField/InputField';
import Spacer from '@components/Spacer';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import CustomSelect from '@components/FormFields/CustomSelect';
import Button from '@components/DesignLibrary/Button';
import Header from '@components/DesignLibrary/Header';
import CheckBox from '@components/DesignLibrary/Atoms/CheckBox';
import { ListAndDetailsSubLayoutContext } from '@layouts/ListAndDetailsSubLayout';
import { useFeatureFlag } from '@providers/FeatureFlags';
import { useRouter } from 'next/router';
import PageContext from '@components/DesignLibrary/PageContext';
import TrashSimple from '@components/Icons/TrashSimple';
import { getUrl, IDENTIFIERS } from '@helpers/urlsHelper';
import * as ApiModels from '@typings/api-models';
import ProgressIndicator from '@components/ProgressIndicator';
import QuestionnairePreview from '@/components/Questionnaire/QuestionnairePreview';
import PencilSimple from '@components/Icons/PencilSimple';
import FilePdf from '@components/Icons/FilePdf';
import TextInput from '@/components/FormFields/TextInput';
import { useOpenFormRequestTemplatePane, useOpenWorkflowPane } from '@/panes/hooks';
import Switch from '@/components/DesignLibrary/Atoms/Switch';
import CollapsibleArea from '@/components/CollapsibleArea';
import GearSix from '@components/Icons/GearSix';
import TextColumns from '@components/Icons/TextColumns';
import Upgrade from '@components/Icons/Upgrade';
import Files from '@components/Icons/Files';
import File from '@components/Icons/File';
import NoodleAI from '@/components/Icons/NoodleAI';
import CheckCircle from '@components/Icons/CheckCircle';
import XCircle from '@components/Icons/XCircle';
import { NOODLE_CASEBUILDER_CREATOR_ID } from '@configuration/client';
import EditFormSummaryInstructionsModal from '@/modals/EditFormSummaryInstructionsModal';
import Modal from '@/components/Modal';
import Tabs from '@/components/DesignLibrary/Tabs';
import RadioInputCards from '@components/FormFields/RadioInputCards';

import { useToast } from '@/hooks';
import CreatePdfInferenceRequest from '@/components/CreatePdfInferenceRequest';
import { ToastTypeVariants } from '@/context/ToastContext';

import ConfirmationModal from '@/modals/ConfirmationModal';
import { AiInferenceRequest } from '@/typings/questionnaires-api-models';
import { regenerateAiRequest } from '@/tsClient/questionnaires';
import useSocketContext from '@/providers/Socket/useSocketContext';
import { SocketMessageType } from '@/providers/Socket';
import s from './CreateEditFormRequestTemplateForm.module.scss';

type Props = {
  formRequestTemplate: Pick<
    FormRequestTemplate,
    | 'canAutoComplete'
    | 'id'
    | 'inheritanceScheme'
    | 'isFollowupEnabled'
    | 'name'
    | 'description'
    | 'provider'
    | 'providerLocator'
    | 'enableAiSummary'
    | 'usesCasebuilderQuestionnaire'
    | 'aiSummaryVoice'
  > | null;
  onSubmit: () => void;
  beforeClose?: () => Promise<void>;
  creatorId: string;
};

const ProviderOptions: { value: string; title: string }[] = [
  { title: 'Anvil', value: FormProviders.Anvil },
  { title: 'Typeform', value: FormProviders.Typeform },
];

const InheritanceSchemeOptions: { value: FormRequestTemplate['inheritanceScheme']; title: string }[] = [
  { title: 'None', value: 'none' },
  { title: 'Inherit from last submission by creator', value: 'creator' },
  { title: 'Inherit from last submission by the customer', value: 'customer' },
];

const CreateEditFormRequestTemplateForm: FC<Props> = ({ beforeClose, formRequestTemplate, onSubmit, creatorId }) => {
  const { reloadListView } = useContext(ListAndDetailsSubLayoutContext);
  const router = useRouter();
  const isEditing = !!formRequestTemplate;
  const [isArchiving, setIsArchiving] = React.useState(false);
  const [title, setTitle] = useState(formRequestTemplate?.name || '');
  const [description, setDescription] = useState(formRequestTemplate?.description || '');
  const [isSaving, setIsSaving] = useState(false);
  const [selectedTab, setSelectedTab] = useState('Settings');
  const [gladeStep, setGladeStep] = useState<undefined | number>(isEditing ? undefined : 0);
  const [gladeOriginal, setGladeOriginal] = useState(false);
  const [isFollowupEnabled] = useState(formRequestTemplate?.isFollowupEnabled || false);
  const [canAutoComplete, setCanAutoComplete] = useState<boolean>(formRequestTemplate?.canAutoComplete ?? false);
  const [inheritanceScheme, setInheritanceScheme] = useState<FormRequestTemplate['inheritanceScheme']>(
    formRequestTemplate?.inheritanceScheme ?? 'none',
  );
  const [provider, setProvider] = useState<FormProviders>(formRequestTemplate?.provider || FormProviders.Noodle);
  const [providerLocator, setProviderLocator] = useState(formRequestTemplate?.providerLocator || '');
  const canEditFormRequestProvider = useFeatureFlag('can-edit-form-request-provider');
  const canUseNoodleQuestionnaire = useFeatureFlag('can-use-noodle-questionnaire');
  const canUpgradeGladeQuestionnairesFeaturFlag = useFeatureFlag('can-upgrade-glade-questionnaires');
  const [enableAiSummary, setEnableAiSummary] = useState(formRequestTemplate?.enableAiSummary ?? true);
  const [useExistingQuestionnaire, setUseExistingQuestinnaire] = useState(!!formRequestTemplate?.providerLocator);
  const openFormRequestTemplatePane = useOpenFormRequestTemplatePane();
  const enableFormRequestAiSummary = useFeatureFlag('form-request-ai-summary');
  const [showEditFormSummaryInstructionsModal, setShowEditFormSummaryInstructionsModal] = useState(false);
  const openWorkflowPane = useOpenWorkflowPane();
  const [titleModalVisible, setTitleModalVisible] = useState(false);

  const [aiSummaryVoiceHook, setAiSummaryVoiceHook] = useState(formRequestTemplate?.aiSummaryVoice);

  const [isRegenerateConfirmationModalOpen, setIsRegenerateConfirmationModalOpen] = useState(false);
  const [fileToRegenerate, setFileToRegenerate] = useState<AiInferenceRequest | null>(null);

  const { getData: updateTemplate } = useNoodleApi(tsClient.formRequests.updateFormRequestTemplate, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Form request updated successfully!'],
  });
  const { getData: postTemplate } = useNoodleApi(tsClient.formRequests.postFormRequestTemplate, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Form request created successfully!'],
  });
  const {
    data: workflows,
    getData: getWorkflowsFn,
    fetchingState: { isFetching: isFetchingWorkflows },
  } = useNoodleApi(tsClient.workflows.getWorkflowsFromBuildingBlock);

  const { getData: postAiSummaryVoice } = useNoodleApi(tsClient.formRequests.updateFormRequestAiSummaryVoice, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Ai summary voice updated successfully'],
  });
  const { getData: archiveFormRequestFn } = useNoodleApi(tsClient.formRequests.archiveFormRequestTemplate, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Form request archived successfully!'],
  });

  const {
    data: aiRequest,
    fetchingState: { isFetching: isFetchingAiRequest },
    getData: getAiRequestFn,
  } = useNoodleApi(tsClient.questionnaires.getAiRequests);

  const { getData: upgradeAllToLatest } = useNoodleApi(tsClient.questionnaires.upgradeAllUsingTemplate, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Started upgrading process'],
  });

  const canTriggerAiFormRequests = useFeatureFlag('can-trigger-ai-form-requests');
  const shouldDisplayAiRequest = canTriggerAiFormRequests
    && isEditing && formRequestTemplate?.provider === FormProviders.Noodle && formRequestTemplate.providerLocator;
  const shouldDisplayAiRequestCompleted = aiRequest?.status === 'completed';
  const shouldDisplayAiRequestFailed = aiRequest?.status === 'failed';
  const shouldDisplayAiRequestProcessing = (aiRequest?.status !== 'completed' && aiRequest?.status !== 'failed' && aiRequest !== null) || isFetchingAiRequest;

  const { addListener, removeListener, isInitialized: isSocketInitialized, joinChannels } = useSocketContext();

  useEffect(() => {
    if (shouldDisplayAiRequest) {
      getAiRequestFn({
        creatorId,
        questionnaireTemplateSlug: formRequestTemplate.providerLocator,
      });

      if (isSocketInitialized) {
        joinChannels([`ai-inference-request/${creatorId}/${formRequestTemplate.providerLocator}`]);
        addListener({
          fn: () => {
            getAiRequestFn({
              creatorId,
              questionnaireTemplateSlug: formRequestTemplate.providerLocator,
            });
          },
          messageType: SocketMessageType.AI_REQUEST_UPDATED,
        });

        return () => {
          removeListener(`ai-inference-request/${creatorId}/${formRequestTemplate.providerLocator}`);
        };
      }
    }
    return () => { };
  }, [shouldDisplayAiRequest, getAiRequestFn, creatorId, formRequestTemplate, isSocketInitialized, joinChannels, addListener, removeListener]);

  const displayToast = useToast();

  const handleAiUploadSuccess = async (): Promise<void> => {
    onSubmit();
    displayToast(ToastTypeVariants.SUCCESS, 'Successfully uploaded PDF');
    if (!isEditing) {
      await reloadListView();
      router.push(getUrl(IDENTIFIERS.DASHBOARD_FORM_REQUESTS));
    } else {
      getAiRequestFn({
        creatorId,
        questionnaireTemplateSlug: formRequestTemplate.providerLocator,
      });
      router.push(
        getUrl(IDENTIFIERS.DASHBOARD_FORM_REQUESTS_EDIT, {
          id: formRequestTemplate.id,
        }),
      );
    }
  };

  const handleAiUploadError = async (): Promise<void> => {
    displayToast(ToastTypeVariants.ERROR, 'Failed to upload PDF');
    onSubmit();
    if (!isEditing) {
      await reloadListView();
      router.push(getUrl(IDENTIFIERS.DASHBOARD_FORM_REQUESTS));
    } else {
      getAiRequestFn({
        creatorId,
        questionnaireTemplateSlug: formRequestTemplate.providerLocator,
      });
      router.push(
        getUrl(IDENTIFIERS.DASHBOARD_FORM_REQUESTS_EDIT, {
          id: formRequestTemplate.id,
        }),
      );
    }
  };

  const handleSave = async (): Promise<void> => {
    setIsSaving(true);

    if (isEditing) {
      await updateTemplate({
        id: formRequestTemplate!.id,
        updates: {
          canAutoComplete,
          description,
          enableAiSummary,
          inheritanceScheme,
          isFollowupEnabled,
          name: title,
          provider,
          providerLocator: provider !== FormProviders.Noodle || useExistingQuestionnaire ? providerLocator : '',
        },
      });
      reloadListView();
    } else {
      await postTemplate({
        canAutoComplete,
        creatorId,
        description,
        enableAiSummary,
        inheritanceScheme,
        isFollowupEnabled,
        name: title,
        provider,
        providerLocator: provider !== FormProviders.Noodle || useExistingQuestionnaire ? providerLocator : '',
      });
      router.push(getUrl(IDENTIFIERS.DASHBOARD_FORM_REQUESTS));
    }
    await beforeClose?.();
    setIsSaving(false);
    onSubmit();
  };

  const handleAiSummaryVoiceSave = async ({ aiSummaryVoice }: { aiSummaryVoice: string | null }): Promise<void> => {
    await postAiSummaryVoice({ aiSummaryVoice, id: formRequestTemplate!.id });
    setAiSummaryVoiceHook(aiSummaryVoice);
    setShowEditFormSummaryInstructionsModal(false);
  };

  const handleAiSummaryVoiceModalClose = (): void => {
    setShowEditFormSummaryInstructionsModal(false);
  };

  const handleUpgradeAll = async (): Promise<void> => {
    if (formRequestTemplate?.providerLocator) {
      const response = await upgradeAllToLatest({ slug: formRequestTemplate.providerLocator });
      if (response.data) {
        console.info('Upgrade all to latest version started', response.data);
      }
    }
  };

  useEffect(() => {
    if (formRequestTemplate) {
      getWorkflowsFn({ page: 1, perPage: 1000, referenceId: formRequestTemplate.id });
    }
  }, [getWorkflowsFn, formRequestTemplate]);

  const archiveFormRequestTemplate = async (): Promise<void> => {
    if (formRequestTemplate) {
      setIsArchiving(true);
      await archiveFormRequestFn({ id: formRequestTemplate.id });
      await reloadListView();
      onSubmit();
      await router.push(getUrl(IDENTIFIERS.DASHBOARD_FORM_REQUESTS));
      setIsArchiving(false);
    }
  };

  const providerOptions = useMemo(
    () => (canUseNoodleQuestionnaire ? [...ProviderOptions, { title: 'Glade', value: FormProviders.Noodle }] : ProviderOptions),
    [canUseNoodleQuestionnaire],
  );

  const hasQuestionnaireTemplate = formRequestTemplate?.provider === FormProviders.Noodle && formRequestTemplate.providerLocator;

  const isCasebuilderCreator = NOODLE_CASEBUILDER_CREATOR_ID === creatorId;
  const usingCasebuilderQuestionnaire = !isCasebuilderCreator && formRequestTemplate?.usesCasebuilderQuestionnaire;
  const canEditQuestionnaireTemplate = hasQuestionnaireTemplate && !usingCasebuilderQuestionnaire;
  const canUpgradeAll = Boolean(
    canUpgradeGladeQuestionnairesFeaturFlag // logged in as accounts@
    && isCasebuilderCreator // in the casebuilder dashboard
    && hasQuestionnaireTemplate // is a noodle-questionnaire based form request
    && formRequestTemplate?.usesCasebuilderQuestionnaire, // is flagged as a global questionnaire.
  );

  const handleEditQuestionnaireTemplate = (): void => {
    if (formRequestTemplate) {
      openFormRequestTemplatePane({ formRequestTemplateSlug: formRequestTemplate.providerLocator });
    }
  };

  const renderPreview = useCallback((): JSX.Element => {
    switch (provider) {
    case FormProviders.Noodle:
      return (
        <QuestionnairePreview slug={providerLocator} usesCasebuilderQuestionnaire={formRequestTemplate?.usesCasebuilderQuestionnaire ?? false} />
      );
    case FormProviders.Anvil:
    default:
      return (
        <iframe
          className={s.iframe}
          src={
            provider === FormProviders.Anvil
              ? `https://app.useanvil.com/form/noodle/${providerLocator}`
              : `https://form.typeform.com/to/${providerLocator}`
          }
        />
      );
    }
  }, [provider, providerLocator, formRequestTemplate?.usesCasebuilderQuestionnaire]);

  const uniqueWorkflows = workflows?.items ? Array.from(new Map(workflows.items.map(workflow => [workflow.slug, workflow])).values()) : [];

  return (
    <>
      {isEditing
        ? (
          <>
            {titleModalVisible && (
              <Modal title="Edit Title and Description" onClose={() => setTitleModalVisible(false)}>
                <div className={s.padding}>
                  <InputField id="title" name="title" values={{ title }} onChange={setTitle} label="Title of form request" hasFixedHeight />
                  <Spacer size={4} />
                  <TextInput id="description" value={description} onChange={setDescription} label="Description of form request" rows={3} />
                  <Spacer size={16} />
                  <Button fullWidth variant="primary" size="md" onClick={() => setTitleModalVisible(false)}>
                  Done
                  </Button>
                </div>
              </Modal>
            )}
            <Header
              title={title}
              description={
                formRequestTemplate.usesCasebuilderQuestionnaire
                  ? (
                    <p className={s.usesCasebuilder}>
                      <NoodleAI size={16} />
                  Provided by Glade Ai
                    </p>
                  )
                  : (
                    description
                  )
              }
              secondaryActions={[
                {
                  icon: PencilSimple,
                  label: 'Edit Title and Description',
                  onClick: () => setTitleModalVisible(true),
                },
                canEditQuestionnaireTemplate && {
                  icon: TextColumns,
                  label: 'Edit Questionnaire',
                  onClick: handleEditQuestionnaireTemplate,
                },
                canUpgradeAll && {
                  icon: Upgrade,
                  label: 'Upgrade All To Latest',
                  onClick: handleUpgradeAll,
                },
                {
                  icon: TrashSimple,
                  isDestructive: true,
                  isFetching: isArchiving,
                  label: 'Archive',
                  onClick: archiveFormRequestTemplate,
                },
              ]}
              tabs={
                providerLocator || hasQuestionnaireTemplate
                  ? [
                    { label: 'Settings', onClick: () => setSelectedTab('Settings'), selected: selectedTab === 'Settings' },
                    { label: 'Preview', onClick: () => setSelectedTab('Preview'), selected: selectedTab === 'Preview' },
                  ]
                  : undefined
              }
            />
            {aiRequest && !!aiRequest.requests?.length
              ? (
                <div className={s.aiTopBanner}>
                  <NoodleAI size={32} />

                  <div className={s.textContainer}>
                    <div>{shouldDisplayAiRequestProcessing ? 'Glade Ai is generating this questionnaire...' : 'Glade Ai finished generating'}</div>
                    <div className={s.secondaryText}>{shouldDisplayAiRequestProcessing ? 'You can leave the page.' : 'Review questionnaire'}</div>
                  </div>

                  {!shouldDisplayAiRequestProcessing && (
                    <div className={s.aiBannerButtonContainer}>
                      <Button variant="primary" size="sm" onClick={handleEditQuestionnaireTemplate}>
                        Review and edit
                      </Button>
                    </div>
                  )}
                </div>
              )
              : null}
            <div className={s.context}>
              <PageContext
                collapsible
                label={
                  isFetchingWorkflows ? 'workflows' : `Used in ${uniqueWorkflows.length} workflow template${uniqueWorkflows.length === 1 ? '' : 's'}`
                }
                items={
                  uniqueWorkflows.map?.((workflow: Pick<ApiModels.Workflow, 'id' | 'name' | 'slug'>) => ({
                    id: workflow.id,
                    onClick: () => openWorkflowPane({ workflowSlug: workflow.slug }),
                    value: workflow.name,
                  })) || []
                }
                isFetching={isFetchingWorkflows}
              />
            </div>
          </>
        )
        : (
          <>
            <div className={s.padding}>
              <InputField id="title" name="title" values={{ title }} onChange={setTitle} label="Title of form request" hasFixedHeight />
              <Spacer size={4} />
              <TextInput id="description" value={description} onChange={setDescription} label="Description of form request" rows={2} />
            </div>
          </>
        )}
      {!isEditing && (
        <Tabs
          inputName="provider"
          onChange={value => {
            setProvider(value as FormProviders);
            setGladeStep(value === 'noodle' ? 0 : undefined);
            if (value === 'noodle') {
              setGladeOriginal(false);
            }
          }}
          checked={providerOptions[2]?.value}
          inputs={providerOptions.map(o => ({
            id: o.value,
            label: o.title,
          }))}
        />
      )}
      {isArchiving && (
        <>
          <Spacer size={16} />
          <ProgressIndicator isCentered />
        </>
      )}
      {selectedTab === 'Preview' && renderPreview()}
      {gladeStep === 0 && (
        <div className={s.padding}>
          <RadioInputCards
            inputName="glade-type"
            onChange={value => {
              setGladeOriginal(value === 'scratch');
            }}
            inputs={[
              {
                icon: File,
                id: 'scratch',
                label: 'Start from scratch',
              },
              {
                defaultChecked: true,
                icon: FilePdf,
                id: 'pdf',
                label: 'Start with PDFs',
              },
            ]}
          />
        </div>
      )}
      {selectedTab === 'Settings' && (gladeStep !== 0 || gladeOriginal) && (
        <>
          {shouldDisplayAiRequest && (
            <>
              <CollapsibleArea title="Forms" Icon={{ Component: Files }} open>
                <section className={s.aiRequestContainer}>
                  {aiRequest?.requests?.length
                    ? aiRequest.requests.map((aiRequestItem, index) => (
                      <div key={aiRequestItem.id || index} className={s.aiRequest}>
                        <div style={{ maxHeight: 24, maxWidth: 24 }}>
                          {shouldDisplayAiRequestProcessing ? <NoodleAI size={24} /> : <FilePdf weight="fill" size={24} />}
                        </div>
                        <div style={{ flex: 1 }}>
                          {shouldDisplayAiRequestProcessing
                            ? (
                              <p className="body-sm-bold">Glade AI is processing {aiRequestItem.name}...</p>
                            )
                            : (
                              <p className="body-sm-bold">{aiRequestItem.name}</p>
                            )}
                        </div>
                        {shouldDisplayAiRequestProcessing
                          ? null
                          : (
                            <>
                              {shouldDisplayAiRequestCompleted && <CheckCircle size={24} weight="fill" color="var(--color-success)" />}
                              {shouldDisplayAiRequestFailed && <XCircle size={24} weight="fill" color="var(--color-error)" />}
                              <Button size="xs" variant="secondary" onClick={() => {
                                setIsRegenerateConfirmationModalOpen(true);
                                setFileToRegenerate(aiRequestItem);
                              }}>
                                Regenerate questionnaire
                              </Button>
                            </>
                          )}
                      </div>
                    ))
                    : null}
                  <CreatePdfInferenceRequest
                    title={title}
                    provider={provider}
                    onError={handleAiUploadError}
                    onSuccess={handleAiUploadSuccess}
                    creatorId={creatorId}
                    questionnaireSlug={formRequestTemplate?.providerLocator}
                    compact
                  />
                </section>
              </CollapsibleArea>
            </>
          )}
          <CollapsibleArea title="Settings" Icon={{ Component: GearSix }}>
            <Spacer />
            <CheckBox
              className={s.settingsRow}
              id="can-auto-complete"
              isChecked={canAutoComplete}
              onChange={setCanAutoComplete}
              label="Auto Submit Form"
            />
            <div className={s.padding}>
              {canEditFormRequestProvider && (
                <>
                  <CustomSelect
                    options={InheritanceSchemeOptions}
                    value={inheritanceScheme}
                    id="inheritance-scheme"
                    onChange={value => setInheritanceScheme(value as FormRequestTemplate['inheritanceScheme'])}
                    label="Inheritance Scheme"
                  />
                  {provider === FormProviders.Noodle && (
                    <>
                      <Spacer size={16} />
                      <CheckBox
                        id="use-existing-noodle-questionnaire"
                        isChecked={useExistingQuestionnaire}
                        onChange={setUseExistingQuestinnaire}
                        label="Use existing Glade Questionnaire"
                      />
                    </>
                  )}
                  {(provider !== FormProviders.Noodle || useExistingQuestionnaire) && (
                    <>
                      <Spacer size={16} />
                      <InputField
                        id="providerLocator"
                        disabled={formRequestTemplate?.usesCasebuilderQuestionnaire}
                        name="providerLocator"
                        values={{ providerLocator }}
                        onChange={setProviderLocator}
                        label={provider === FormProviders.Noodle ? 'Questionnaire Template Slug' : 'Provider Locator'}
                        hasFixedHeight
                      />
                    </>
                  )}
                </>
              )}
            </div>
          </CollapsibleArea>
          {enableFormRequestAiSummary && (
            <div className={s.summarizeResponses}>
              <div>
                <NoodleAI size={20} />
                <div>
                  <span className="body-md-bold">Summarize Responses with Ai</span>
                  <span className="caption">
                    Your Glade Ai Copilot will summarize the answers to this questionnaire from your client and share it with you.
                  </span>
                </div>
              </div>
              <Switch onChange={setEnableAiSummary} label="" isChecked={enableAiSummary} name="" />
            </div>
          )}
          <div className={s.AddInstructionsBtn}>
            <Button
              variant="ai"
              size="md"
              onClick={() => {
                setShowEditFormSummaryInstructionsModal(true);
              }}
            >
              Add instructions
            </Button>
            {showEditFormSummaryInstructionsModal && (
              <EditFormSummaryInstructionsModal
                onSave={handleAiSummaryVoiceSave}
                aiSummaryVoice={aiSummaryVoiceHook ?? null}
                onClose={handleAiSummaryVoiceModalClose}
              />
            )}
          </div>
        </>
      )}
      {!gladeOriginal && selectedTab === 'Settings' && provider === FormProviders.Noodle && !isEditing && (
        <div style={{ padding: '0 16px' }}>
          <CreatePdfInferenceRequest
            title={title}
            provider={provider}
            onError={handleAiUploadError}
            onSuccess={handleAiUploadSuccess}
            creatorId={creatorId}
          />
        </div>
      )}
      {selectedTab === 'Settings' && (
        <div className={s.padding}>
          <Button
            fullWidth
            variant="primary"
            size="md"
            onClick={handleSave}
            loading={isSaving}
            disabled={!title || (!providerLocator && provider !== FormProviders.Noodle)}
          >
            Save
          </Button>
        </div>
      )}

      {isRegenerateConfirmationModalOpen && (
        <ConfirmationModal
          onClose={() => { setIsRegenerateConfirmationModalOpen(false);
            setFileToRegenerate(null);
          }}
          text={`Are you sure you want to regenerate the questionnaire for ${fileToRegenerate?.name ?? 'this PDF'}?`}
          onSubmit={async () => {
            await regenerateAiRequest({creatorId,questionnaireTemplateSlug: formRequestTemplate?.providerLocator??"",requestId:fileToRegenerate?.id??""});
            setIsRegenerateConfirmationModalOpen(false);
            setFileToRegenerate(null);
          }}
          cta='Regenerate'
        />
      )}
    </>
  );
};

export default CreateEditFormRequestTemplateForm;
